<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:1rem">
      <div>
        <button class="mdm-back" @click="$router.push('/mdm')">
          <font-awesome-icon :icon="['fas', 'arrow-left']" /> CAGISTA MDM
        </button>
        <h3 class="mb-1 mt-1">Policyer</h3>
        <div style="font-style: italic; color:var(--bo-muted); max-width:680px">
          Policyene som enhetene tildeles ved enrollment. Opprett og rediger dem her; «Lagre og oppdater policyer»
          pusher endringene til AMAPI.
        </div>
        <!-- Same guard as the device page, applied to what THIS page writes. Every policy on this enterprise
             is a production policy — there is no name to match on, so the whole page is read-only here. -->
        <div v-if="isLocalDev()" class="mdm-devbanner mt-2">
          <font-awesome-icon :icon="['fas', 'lock']" />
          <span>
            Kjører fra <code>localhost</code> mot samme AMAPI-enterprise som produksjon. Siden er
            <strong>skrivebeskyttet</strong> her: policyer kan leses, men ikke opprettes, endres, lagres eller
            slettes. «Lagre og oppdater policyer» skriver AMAPI-policyene ut fra dette miljøets innstillinger,
            og hele flåten tar dem i bruk ved neste synk — bruk produksjons-Backoffice.
          </span>
        </div>
      </div>
      <span v-if="status && status.configured" class="mdm-enterprise">
        Enterprise <code>{{ status.enterpriseId }}</code>
      </span>
    </div>

    <div v-if="!loading && status && !status.configured" class="alert alert-warning mt-4">
      <strong>Android Management API er ikke konfigurert.</strong>
      Se <code>CagistaServices/docs/amapi-setup-runbook.md</code>.
    </div>

    <template v-if="!loading && status && status.configured">
      <!-- Navigator OUTSIDE the ConfigLock: a disabled <fieldset> disables every control inside it, so a
           navigator in there would leave the page opening locked with its own policy list dead — you would
           have to unlock the edit guard just to READ a policy. Browsing is not editing. The one control
           that does start an edit, «Ny policy», is gated on policiesLocked instead. -->
      <div class="mdm-split mt-4">
        <!-- Policy navigator. A wrapping tab strip stops being a list once the fleet grows: 11 policies
           and their auto-generated *_PROV/_UPDATE twins render 24 tabs over six rows, so the page opens
           on nothing but tabs. Here the policies are sorted by name, each twin is nested under the policy
           it belongs to (collapsed by default), and the search filters both. -->
        <aside class="mdm-nav">
          <div class="position-relative">
            <input class="form-control form-control-sm" type="text" v-model="policySearch"
                   placeholder="Søk policy…" style="padding-right:28px" aria-label="Søk policy" />
            <a v-if="policySearch" class="bo_search-clear mdm-nav__clear" title="Tøm søket" @click="policySearch = ''">
              <font-awesome-icon :icon="['fas', 'times']" />
            </a>
          </div>
          <div class="mdm-nav__count">
            {{ roles.length }} policy{{ roles.length === 1 ? '' : 'er' }}<template v-if="systemPolicies.length"> · {{ systemPolicies.length }} systemvariant{{ systemPolicies.length === 1 ? '' : 'er' }}</template>
          </div>

          <button class="mdm-navitem mdm-navitem--general"
                  :class="{ 'mdm-navitem--active': activeTab === 'general' }" @click="activeTab = 'general'">
            <font-awesome-icon :icon="['fas', 'sliders-h']" class="mdm-navitem__kind" />
            <span class="mdm-navitem__name">Generelt</span>
          </button>

          <div class="mdm-nav__list">
            <div v-if="!filteredTree.length && !filteredOrphans.length" class="mdm-nav__empty">
              {{ policySearch ? 'Ingen treff.' : 'Ingen policyer ennå.' }}
            </div>

            <template v-for="node in filteredTree" :key="'nav-' + node.name">
              <div class="mdm-navrow">
                <button class="mdm-nav__caret" :class="{ 'mdm-nav__caret--hidden': !node.variants.length }"
                        :tabindex="node.variants.length ? 0 : -1"
                        :title="isBaseExpanded(node) ? 'Skjul systemvarianter' : 'Vis systemvarianter (' + node.variants.length + ')'"
                        @click="toggleBase(node.name)">
                  <font-awesome-icon :icon="['fas', isBaseExpanded(node) ? 'chevron-down' : 'chevron-right']" />
                </button>
                <button class="mdm-navitem" :class="{ 'mdm-navitem--active': activeTab === node.name }"
                        :title="node.name + ' — ' + kindLabel(kinds[node.name])" @click="activeTab = node.name">
                  <font-awesome-icon v-if="kinds[node.name] === 'MAINTENANCE'" :icon="['fas', 'wrench']"
                                     class="mdm-navitem__kind" title="Vedlikeholdspolicy" />
                  <span class="mdm-navitem__name">{{ node.name }}</span>
                  <!-- The count needs the icon: a bare number here reads just as easily as "devices on
                       this policy", which is a different number entirely. -->
                  <span class="mdm-navitem__meta" :title="appCount(node.name) + ' app(er) i denne policyen'">
                    <font-awesome-icon :icon="['fas', 'th-large']" class="mdm-navitem__metaicon" />{{ appCount(node.name) }}
                  </span>
                </button>
              </div>
              <button v-for="v in (isBaseExpanded(node) ? node.variants : [])" :key="'nav-' + v.name"
                      class="mdm-navitem mdm-navitem--variant"
                      :class="{ 'mdm-navitem--active': activeTab === v.name }"
                      :title="v.name + ' — systemgenerert (skrivebeskyttet)'" @click="activeTab = v.name">
                <span class="mdm-navitem__name">{{ variantSuffix(v.name) }}</span>
                <span class="mdm-navitem__meta">{{ variantLabel(v.name) }}</span>
              </button>
            </template>

            <!-- Twins whose base policy is gone: pure leftovers, and the only place they are visible. -->
            <template v-if="filteredOrphans.length">
              <div class="mdm-nav__group">Uten grunnpolicy</div>
              <button v-for="v in filteredOrphans" :key="'navo-' + v.name"
                      class="mdm-navitem mdm-navitem--variant mdm-navitem--orphan"
                      :class="{ 'mdm-navitem--active': activeTab === v.name }"
                      title="Systemgenerert policy — grunnpolicyen finnes ikke lenger" @click="activeTab = v.name">
                <span class="mdm-navitem__name">{{ v.name }}</span>
              </button>
            </template>
          </div>

          <button class="mdm-nav__add" :disabled="policiesLocked"
                  :title="isLocalDev() ? 'Skrivebeskyttet fra localhost — bruk produksjons-Backoffice'
                                       : (policiesLocked ? 'Trykk «Lås opp» for å opprette en policy' : 'Legg til ny policy')"
                  @click="openNewPolicy">
            <font-awesome-icon :icon="['fas', 'plus']" /> Ny policy
          </button>
        </aside>

        <div class="mdm-panels">
          <config-lock ref="lock" :configured="isConfigured" :read-only="isLocalDev()"
                       read-only-text="Skrivebeskyttet — Backoffice kjører fra localhost mot produksjonens AMAPI-enterprise."
                       @lock-change="policiesLocked = $event">
          <div class="mdm-settings-card">
        <div class="d-flex justify-content-between align-items-center flex-wrap mdm-settings-head" style="gap:0.5rem">
          <div class="mdm-orient-card__title mb-0">Policy-innstillinger</div>
          <button class="bo_btn" @click="saveSettings" :disabled="savingOrientation">
            {{ savingOrientation ? 'Lagrer…' : 'Lagre og oppdater policyer' }}
          </button>
        </div>
        <div style="color:var(--bo-muted); font-size:0.85rem" class="mb-3">
          Endringer lagres og pushes til policyene (re-seed). Enhetene tar dem i bruk ved neste synk.
        </div>
        <p v-if="orientationMessage" :class="orientationError ? 'text-danger' : 'text-success'" class="mb-3">{{ orientationMessage }}</p>

        <!-- Policy-innstillingene ligger i Couchbase (per miljø), AMAPI-enterprisen er delt. Et lokalt
             backend mot produksjons-enterprisen ser derfor slik ut: alle policyene finnes, ingen av dem er
             kjent her, så alt faller gjennom til det skrivebeskyttede system-panelet. Ikke «fiks» det ved å
             opprette dem — se advarselen. -->
        <div v-if="!roles.length && systemPolicies.length" class="alert alert-warning py-2 px-3 mb-3"
             style="font-size:0.85rem">
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
          <b>Ingen av policyene er registrert i dette miljøets policy-innstillinger.</b>
          AMAPI-enterprisen har {{ systemPolicies.length }} policyer, men ingen av dem står i innstillingene her,
          så alle vises som systemgenererte og skrivebeskyttede.
          <template v-if="isLocalDev()">
            Du kjører fra <code>localhost</code> mot samme AMAPI-enterprise som produksjon, mens
            policy-innstillingene ligger i dette miljøets egen database — <b>det er den normale forklaringen,
            og i produksjons-Backoffice vises de samme policyene som redigerbare.</b>
          </template>
          <template v-else>
            Innstillingene og AMAPI-enterprisen hører altså ikke sammen — enten peker miljøet på en annen
            enterprise enn det ble satt opp med, eller listen over policyer er borte fra innstillingene.
          </template>
          <br />
          <b>Ikke opprett dem på nytt her for å få dem redigerbare.</b> «Ny policy» + «Lagre og oppdater
          policyer» skriver AMAPI-policyen ut fra innstillingene på denne siden, så en policy som
          <code>KDS</code> ville blitt overskrevet med standardmalen — og hele flåten som står på den tar den
          i bruk ved neste synk. Rediger policyer der innstillingene deres faktisk ligger.
        </div>

        <p v-if="crudMessage" :class="crudError ? 'text-danger' : 'text-success'" class="mb-3">{{ crudMessage }}</p>

        <!-- New-policy form (structured — name + kind; the per-policy controls appear once it's created) -->
        <div v-if="showNewPolicy" class="mdm-newpolicy mb-3">
          <div class="fw-semibold mb-2">Ny policy</div>
          <div class="d-flex align-items-end gap-2 flex-wrap">
            <div>
              <label class="mdm-app-sublabel d-block mb-1">Navn</label>
              <input class="form-control form-control-sm" style="max-width:240px; text-transform:uppercase"
                     v-model="newPolicy.name" @input="newPolicy.name = (newPolicy.name || '').toUpperCase()"
                     placeholder="F.EKS. LAGER_NETTBRETT" />
            </div>
            <div>
              <label class="mdm-app-sublabel d-block mb-1">Policytype</label>
              <select class="form-select form-select-sm" style="max-width:220px" v-model="newPolicy.kind">
                <option value="CUSTOM_LAUNCHER">Egen launcher</option>
                <option value="MULTI_APP">Fler-app Home (Google Launcher)</option>
                <option value="SINGLE_APP">Enkelt-app kiosk mode</option>
                <option value="MAINTENANCE">Vedlikehold (opplåst)</option>
              </select>
            </div>
            <button class="bo_btn" :disabled="creatingPolicy || !newPolicyNameValid" @click="createPolicy">
              {{ creatingPolicy ? 'Oppretter…' : 'Opprett' }}
            </button>
            <button class="bo_btn_secondary" :disabled="creatingPolicy" @click="showNewPolicy = false">Avbryt</button>
          </div>
          <div v-if="newPolicy.name && !newPolicyNameValid" style="color:#c0392b; font-size:0.78rem" class="mt-1">
            Bruk STORE bokstaver, tall og understrek (2–31 tegn), start med bokstav. Kan ikke slutte på _PROV eller _UPDATE.
          </div>
        </div>

        <!-- Generelt: global toggle + at-a-glance overview -->
        <div v-show="activeTab === 'general'">
          <div class="d-flex align-items-baseline justify-content-between flex-wrap mb-2" style="gap:0.5rem">
            <div class="fw-semibold">Oversikt</div>
            <span style="color:#aaa; font-size:0.78rem">Klikk en rad for å redigere policyen</span>
          </div>
          <div class="table-responsive mb-4">
            <table class="table table-sm mdm-overview align-middle mb-0">
              <thead>
                <tr>
                  <th>Policy</th><th>Type</th><th>Orientering</th><th>Oppdatering</th>
                  <th class="text-end">Apper</th><th>Systemvarianter</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!roles.length && !systemPolicies.length">
                  <td colspan="6" class="text-muted" style="font-style:italic; padding:0.75rem">
                    Ingen policyer ennå — trykk «+ Ny policy» for å opprette din første.
                  </td>
                </tr>
                <tr v-for="node in policyTree.tree" :key="'ov-' + node.name" style="cursor:pointer"
                    @click="activeTab = node.name">
                  <td>
                    <font-awesome-icon v-if="kinds[node.name] === 'MAINTENANCE'" :icon="['fas', 'wrench']"
                                       class="mdm-navitem__kind me-1" />
                    <code>{{ node.name }}</code>
                  </td>
                  <td>{{ kindLabel(kinds[node.name]) }}</td>
                  <td>{{ orientationLabel(orientation[node.name]) }}</td>
                  <td>{{ systemUpdate[node.name] === 'AUTOMATIC' ? 'Umiddelbart' : 'Natt-vindu' }}</td>
                  <td class="text-end">{{ appCount(node.name) }}</td>
                  <td>
                    <span v-if="!node.variants.length" style="color:#c8ccd0">—</span>
                    <span v-for="v in node.variants" :key="'ovv-' + v.name" class="mdm-varchip"
                          role="button" tabindex="0"
                          :title="v.name + ' — ' + variantLabel(v.name) + ' (systemgenerert, skrivebeskyttet)'"
                          @click.stop="activeTab = v.name"
                          @keyup.enter.stop="activeTab = v.name">{{ variantSuffix(v.name) }}</span>
                  </td>
                </tr>
                <!-- Auto-generated policies whose base is gone. Leftovers only — kept visible so they can be
                     found and deleted; a live twin belongs in its policy's row above. -->
                <tr v-for="sp in policyTree.orphans" :key="'ovsys-' + sp.name" class="mdm-overview__system"
                    style="cursor:pointer" @click="activeTab = sp.name"
                    title="Systemgenerert policy — skrivebeskyttet. Klikk for detaljer.">
                  <td><code>{{ sp.name }}</code></td>
                  <td colspan="5" class="text-muted" style="font-style:italic">
                    System · auto-generert — grunnpolicyen
                    <code>{{ baseOf(sp.name) || '?' }}</code> finnes ikke lenger
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="fw-semibold mb-2">Skjerm (gjelder alle policyer)</div>
          <!-- The project's own switch component: Bootstrap's .form-switch is overridden in
               backoffice.css, which sets `.form-switch input { display: none }` and draws the toggle from
               the <i>. Bootstrap markup here renders a hidden checkbox and nothing else. -->
          <div class="form-switch-wrapper mb-4" style="clear:both; overflow:auto; float:none">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="stayOnCharging" />
                <i></i>
                <span>Hold skjermen på mens enheten lader
              <span style="color:var(--bo-muted); font-size:0.82rem">— anbefalt for enheter som alltid står i strøm</span></span>
              </div>
            </label>
          </div>
          <div class="fw-semibold mb-1">Systemoppdatering — vedlikeholdsvindu</div>
          <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
            Når policyer satt til «Natt-vindu» installerer OS-oppdateringer. Gjelder alle slike policyer.
          </div>
          <div class="d-flex align-items-center gap-2 mb-4" style="flex-wrap:wrap">
            <select class="form-select form-select-sm" style="max-width:78px" v-model.number="windowStartH">
              <option v-for="h in hourOptions" :key="'sh' + h" :value="h">{{ pad2(h) }}</option>
            </select>
            <span style="color:var(--bo-muted)">:</span>
            <select class="form-select form-select-sm" style="max-width:78px" v-model.number="windowStartM">
              <option v-for="m in minuteOptions" :key="'sm' + m" :value="m">{{ pad2(m) }}</option>
            </select>
            <span style="color:var(--bo-muted)" class="mx-1">til</span>
            <select class="form-select form-select-sm" style="max-width:78px" v-model.number="windowEndH">
              <option v-for="h in hourOptions" :key="'eh' + h" :value="h">{{ pad2(h) }}</option>
            </select>
            <span style="color:var(--bo-muted)">:</span>
            <select class="form-select form-select-sm" style="max-width:78px" v-model.number="windowEndM">
              <option v-for="m in minuteOptions" :key="'em' + m" :value="m">{{ pad2(m) }}</option>
            </select>
          </div>

          <!-- The 06:00 restart (CagistaServices DailyDeviceReboot). Saved at once on its own endpoint, NOT with
               «Lagre og oppdater policyer»: it changes no policy, and that button's parallel read-modify-writes of
               the policy settings are no place for a switch the fleet's mornings depend on. -->
          <div class="fw-semibold mb-1">Daglig omstart</div>
          <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
            Sender «Reboot» til alle kasser, kiosker og kjøkkenskjermer (KDS) hver morgen kl. {{ dailyReboot.time }},
            slik «Reboot» på MDM-siden gjør for én enhet. Lagres med en gang du slår den av eller på; «Lagre og
            oppdater policyer» trengs ikke.
          </div>
          <p v-if="dailyRebootLoadError" class="text-danger mb-4" style="font-size:0.85rem">{{ dailyRebootLoadError }}</p>
          <div v-else-if="dailyReboot.loaded" class="mb-4">
            <div class="form-switch-wrapper mb-2" style="clear:both; overflow:auto; float:none">
              <label class="form-switch">
                <div class="form-switch-container">
                  <!-- :checked, not v-model: the switch shows the SAVED state until the server confirms. -->
                  <input type="checkbox" :checked="dailyReboot.enabled" :disabled="savingDailyReboot"
                         @change="toggleDailyReboot($event)" />
                  <i></i>
                  <span>Start kasser, kiosker og KDS på nytt kl. {{ dailyReboot.time }}</span>
                </div>
              </label>
            </div>
            <p v-if="dailyRebootMessage" :class="dailyRebootError ? 'text-danger' : 'text-success'" class="mb-2"
               style="font-size:0.85rem">{{ dailyRebootMessage }}</p>
            <div class="mdm-reboot">
              <div v-if="!dailyReboot.enabled" class="mdm-reboot__off">
                <font-awesome-icon :icon="['fas', 'pause-circle']" class="me-1" />
                Avslått{{ dailyRebootChangedLabel }}. Ingen enheter startes på nytt før den slås på igjen.
              </div>
              <div class="mdm-reboot__row">
                <span class="mdm-reboot__label">Gjelder</span>
                <span v-if="!dailyRebootPolicies.length" style="font-style:italic">
                  ingen policy med TastyPOS, TastyKIOSK eller TastyKDS
                </span>
              </div>
              <!-- Whether the app comes back after the restart is the policy's business: a pinned app (kiosk mode)
                   starts by itself, under the launcher only «Standard-app ved oppstart» starts it. The server says
                   which; a KDS without it shows no orders until someone opens it. The name opens the policy, where
                   the setting is. A link, not a button, so it still works while the page is locked. -->
              <ul v-if="dailyRebootPolicies.length" class="mdm-reboot__policies">
                <li v-for="p in dailyRebootPolicies" :key="'rb-' + p.name">
                  <a class="mdm-reboot__policylink" :title="'Åpne ' + p.name" @click="activeTab = p.name"><code>{{ p.name }}</code></a>
                  {{ rebootRoleLabel(p.role) }}
                  <span v-if="p.startsAfterBoot" class="mdm-reboot__hint">· appen starter av seg selv etter omstarten</span>
                  <span v-else class="mdm-reboot__warn">
                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />{{ rebootNoStartText(p.role) }}
                    Sett «Standard-app ved oppstart» i policyen.
                  </span>
                </li>
              </ul>
              <div class="mdm-reboot__hint">
                En kasse, kiosk eller kjøkkenskjerm kjennes igjen på appen i policyen (TastyPOS, TastyKIOSK,
                TastyKDS), så en ny policy med en av dem kommer med av seg selv. Kundeskjermer (ODS) startes ikke på
                nytt. Det gjør heller ikke en enhet som ikke er aktiv, er merket RMA, står på en vedlikeholdspolicy
                eller er midt i «Tving oppdatering». En enhet som er av kl. {{ dailyReboot.time }}, startes ikke på
                nytt senere på dagen.
              </div>
              <div class="mdm-reboot__row">
                <span class="mdm-reboot__label">Siste kjøring</span>
                <span :class="{ 'mdm-reboot__bad': dailyRebootLastRunBad }">{{ dailyRebootLastRunLabel }}</span>
              </div>
              <ul v-if="dailyRebootFailures.length" class="mdm-reboot__failures">
                <li v-for="(f, i) in dailyRebootFailures" :key="'rbf-' + i">
                  <code>{{ f.merchant || '—' }}</code> {{ f.name ? '«' + f.name + '»' : f.device }}
                  <span style="color:var(--bo-muted)">({{ f.policy || 'ingen policy' }})</span>: {{ f.error }}
                </li>
              </ul>
              <div v-if="dailyReboot.lastRun && !dailyReboot.lastRun.switchedOff" class="mdm-reboot__hint mt-1">
                «Sendt» betyr at Google tok imot kommandoen. Enheten starter på nytt når den henter den, innen ti
                minutter. Hver enhet står i <code>reboot.log</code> på serveren.
              </div>
            </div>
          </div>
        </div>

        <!-- Read-only panel per auto-generated *_PROV policy — shown so every policy name is visible. -->
        <div v-for="sp in systemPolicies" :key="'panel-' + sp.name" v-show="activeTab === sp.name">
          <div class="d-flex align-items-center flex-wrap mb-3" style="gap:0.5rem">
            <code class="mdm-panel-title">{{ sp.name }}</code>
            <span class="mdm-kind-badge">System · auto-generert</span>
          </div>
          <p class="text-muted" style="max-width:760px">
            Denne policyen genereres og vedlikeholdes automatisk av systemet — den kan ikke redigeres her.
            <template v-if="sp.name.endsWith('_PROV')"> Provisjoneringsvariant av <code>{{ sp.name.slice(0, -5) }}</code>: den tvangsinstallerer «Installert, skjult»-appen ved førstegangsoppsett, og enheten flyttes automatisk tilbake til <code>{{ sp.name.slice(0, -5) }}</code> når appen er installert (ikonet skjules).</template>
            <template v-else-if="sp.name.endsWith('_UPDATE')"> Oppdateringsvariant av <code>{{ sp.name.slice(0, -7) }}</code>, laget automatisk av «Tving oppdatering»: en kopi der én app er <b>utelatt</b> fra applisten, slik at Android Device Policy avinstallerer den (<code>installType: BLOCKED</code> gjør det ikke — prøvd på flåten 04.08.2026). Enheten flyttes tilbake med én gang appen er borte, og Play skal da installere siste versjon. <b>Avinstalleringen sletter appdata</b>, så terminalen må normalt logges inn på nytt. <b>Ikke slett policyen mens en enhet står på den</b> — den lages på nytt ved behov. Er oppdateringen ferdig, er dette bare en rest, og «Slett rest» rydder den bort. Varianten lages mot den policyen enheten faktisk sto på, så testing fra en vedlikeholdspolicy etterlater sin egen tvilling.</template>
          </p>
          <div class="d-flex flex-wrap" style="gap:0.5rem">
            <a class="bo_btn_secondary mdm-btn-a" @click="showRaw(sp.raw, sp.name)">Vis rå (AMAPI)</a>
            <!-- Only _UPDATE variants can be removed. They are leftovers from a finished «Tving
                 oppdatering» and are rebuilt on demand, so deleting one loses nothing. _PROV variants
                 belong to the managed set and are re-seeded, so removing one would only break
                 enrolment until the next seed. -->
            <button v-if="sp.name.endsWith('_UPDATE')" class="bo_btn_secondary"
                    title="Rester etter en fullført tvungen oppdatering. Lages på nytt neste gang den trengs."
                    @click="deleteVariantPolicy(sp.name)">
              <font-awesome-icon :icon="['fas', 'trash']" class="me-1" /> Slett rest
            </button>
          </div>
        </div>

        <!-- One panel per policy: its orientation + update mode + apps together -->
        <div v-for="role in roles" :key="'panel-' + role" v-show="activeTab === role">
          <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:0.5rem">
            <div class="d-flex align-items-center flex-wrap" style="gap:0.5rem">
              <code class="mdm-panel-title">{{ role }}</code>
              <span class="mdm-kind-badge">{{ kindLabel(kinds[role]) }}</span>
            </div>
            <div class="d-flex align-items-center flex-wrap" style="gap:0.5rem">
              <a class="bo_btn_secondary mdm-btn-a" :class="{ 'mdm-btn-a--off': !rawPolicyFor(role) }"
                 :title="rawPolicyFor(role) ? 'Vis rå (AMAPI)' : 'Ikke lagret til AMAPI ennå — «Lagre og oppdater policyer» først'"
                 @click="rawPolicyFor(role) && showRawPolicy(role)">
                <font-awesome-icon :icon="['fas', 'code']" class="me-1" /> Vis rå
              </a>
              <button class="bo_btn_secondary mdm-del-policy" @click="deletePolicyStructured(role)">
                <font-awesome-icon :icon="['fas', 'trash']" class="me-1" /> Slett policy
              </button>
            </div>
          </div>

          <div v-if="kinds[role] === 'MAINTENANCE'" class="alert alert-secondary py-2 px-3 mb-4" style="font-size:0.85rem">
            <font-awesome-icon :icon="['fas', 'info-circle']" class="me-1" />
            Opplåst vedlikeholds-policy — ingen kiosk-lås; appene installeres og er synlige (ingen skjuling/kiosk-feste),
            og utvikler-/USB-feilsøking er tillatt. Legg til kun appene denne policyen trenger under. Koble den til en
            kiosk-policy nedenfor på den policyen (feltet «Vedlikeholdspolicy»), så bytter enhetene hit ved «Avslutt kiosk-modus».
          </div>

          <div v-if="kinds[role] === 'CUSTOM_LAUNCHER'" class="alert alert-secondary py-2 px-3 mb-4" style="font-size:0.85rem">
            <font-awesome-icon :icon="['fas', 'info-circle']" class="me-1" />
            Egen launcher (TastyLAUNCHER) — merket hjemmeskjerm som erstatter Googles innebygde kiosk-launcher (fjerner
            tre-prikk-menyen «Managed device info»). Appene i «Apper» under vises som fliser på hjem-skjermen; skjulte
            apper (f.eks. VIVA) installeres men vises ikke. TastyLAUNCHER installeres automatisk som hjem-app.
          </div>

          <div class="fw-semibold mb-1">Skjermorientering</div>
          <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
            Managed-config <code>screen_orientation</code> — appen låser orienteringen (AMAPI kan ikke selv).
          </div>
          <select class="form-select form-select-sm mb-4" style="max-width:280px" v-model="orientation[role]">
            <option value="LANDSCAPE">Liggende (landscape)</option>
            <option value="PORTRAIT">Stående (portrait)</option>
            <option value="AUTO">Automatisk</option>
          </select>

          <div class="fw-semibold mb-1">Skjermen alltid på</div>
          <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
            Managed-config <code>keep_screen_on</code> — appen holder skjermen våken
            (<code>FLAG_KEEP_SCREEN_ON</code>). AMAPI har ingen skjerm-timeout selv, og «hold skjermen på mens
            enheten lader» (Generelt) gjelder bare når enheten står i strøm — en håndholdt kasse går på batteri
            gjennom vakta. <strong>Av-knappen virker fortsatt:</strong> skjermen slukner når personalet slukker
            den, aldri av seg selv.
          </div>
          <div class="form-switch-wrapper mb-4" style="clear:both; overflow:auto; float:none">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="keepScreenOn[role]" />
                <i></i>
                <span>Hold skjermen på
              <span style="color:var(--bo-muted); font-size:0.82rem">— på som standard; slå av for enheter som skal sove normalt</span></span>
              </div>
            </label>
          </div>

          <!-- Kiosk-chrome controls: only for locked kiosk-launcher policies. NOT for MAINTENANCE (unlocked) nor
               CUSTOM_LAUNCHER (kioskCustomization is ignored when our own KIOSK launcher owns the home UI). -->
          <template v-if="!['MAINTENANCE', 'CUSTOM_LAUNCHER'].includes(kinds[role])">
            <div class="fw-semibold mb-1">Navigasjonsknapper</div>
            <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
              Systemlinjen (hjem/tilbake/oversikt) — <code>kioskCustomization.systemNavigation</code>.
            </div>
            <select class="form-select form-select-sm mb-4" style="max-width:280px" v-model="systemNavigation[role]">
              <option value="NAVIGATION_ENABLED">Vis (hjem/tilbake/oversikt)</option>
              <option value="HOME_BUTTON_ONLY">Kun hjem</option>
              <option value="NAVIGATION_DISABLED">Skjul</option>
            </select>
          </template>

          <!-- Maintenance link: applies to every kiosk-launcher policy (incl. CUSTOM_LAUNCHER), just not MAINTENANCE. -->
          <template v-if="kinds[role] !== 'MAINTENANCE'">
            <div class="fw-semibold mb-1">Vedlikeholdspolicy</div>
            <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
              Policyen enheten byttes til ved «Avslutt kiosk-modus».
              <span v-if="!maintenanceOptions.length">Opprett først en policy av typen «Vedlikehold».</span>
            </div>
            <select class="form-select form-select-sm mb-4" style="max-width:280px" v-model="maintenancePolicy[role]"
                    :disabled="!maintenanceOptions.length">
              <option value="">(ingen)</option>
              <option v-for="m in maintenanceOptions" :key="'mp-' + role + '-' + m" :value="m">{{ m }}</option>
            </select>
          </template>

          <div class="fw-semibold mb-1">Systemoppdatering</div>
          <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
            Natt-vindu (tidspunkt settes i <strong>Generelt</strong>) eller umiddelbart. Styrer policyens
            <code>systemUpdate</code>.
          </div>
          <select class="form-select form-select-sm mb-4" style="max-width:280px" v-model="systemUpdate[role]">
            <option value="WINDOWED">Natt-vindu ({{ updateWindowStart }}–{{ updateWindowEnd }})</option>
            <option value="AUTOMATIC">Umiddelbart</option>
          </select>

          <div class="d-flex align-items-baseline justify-content-between flex-wrap mb-1">
            <div class="fw-semibold">Apper</div>
            <span style="color:#aaa; font-size:0.78rem">{{ appCount(role) }} app(er)</span>
          </div>
          <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
            Hvordan appene installeres og vises i kiosk-modus:
            <strong>Synlig</strong> = vises i launcheren (merchant kan åpne den) ·
            <strong>Installert, skjult (auto)</strong> = installeres automatisk ved første oppstart, deretter skjult
            (anbefalt for betalingsappen på POS — se under) ·
            <strong>Tillatt, skjult</strong> = kjørbar via POS-appen (f.eks. VIVA/Softpay), men installeres ikke av seg selv ·
            <strong>Blokkert</strong> = ikke tillatt. Tom pakke ignoreres.
          </div>
          <div v-if="hasHiddenApp(role) && kinds[role] !== 'MAINTENANCE'" style="color:var(--bo-muted); font-size:0.8rem" class="mb-2">
            <font-awesome-icon :icon="['fas', 'info-circle']" class="me-1" />
            «Installert, skjult (auto)» enroller enheter på en egen <code>{{ role }}_PROV</code>-policy som tvangsinstallerer
            appen, og flytter dem automatisk tilbake til <code>{{ role }}</code> når appen er på plass (ikon skjules).
          </div>
          <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
            <strong>Oppdatering</strong> — hvor fort en ny appversjon rulles ut fra managed Play:
            <strong>Standard</strong> = Play oppdaterer først når enheten er på Wi-Fi, lader, er inaktiv OG appen ikke er
            i forgrunnen, og sjekker bare ca. én gang i døgnet — på en kasse som står åpen hele dagen kan en ny versjon
            derfor bli liggende i dagevis ·
            <strong>Høy prioritet (straks)</strong> = rulles ut så snart Play ser releasen (Google oppgir inntil et døgn ved
            store utrullinger, som regel mye raskere); appen kan bli stoppet og oppdatert mens den er i bruk, så legg
            releaser utenom rush ·
            <strong>Utsatt</strong> = holder oppdateringer tilbake i opptil 90 dager, så installerer Play uansett.
            Dette er normalinnstillingen for flåten: <code>POS_DESKTOP_PILOT</code> står på «Høy prioritet» og får
            releasen først, resten holdes igjen til piloten har stått et skift uten problemer. Merk at bremsen er en
            <em>forsinkelse</em>, ikke en lås — den kan ikke holde en merchant på en gammel versjon — og at «Tving
            oppdatering» går gjennom uansett, siden det er en installasjon og ikke en oppdatering.
            <br />
            Alle <code>FORCE_INSTALLED</code>-apper får i tillegg løftet Plays <em>installasjonsbegrensninger</em>
            (inaktiv / lader / umålt nett) automatisk når policyen lagres. Uten det holder Play oppdateringen tilbake så
            lenge kassa er i bruk — skjermen er på og appen ligger i forgrunnen, så enheten blir aldri «inaktiv». Ingenting
            å skru på; det settes av policy-generatoren.
            <br />
            <strong>Min. versjon (versionCode)</strong> er et hardt gulv: enheten regnes som ikke-etterlevende til den
            har minst denne versjonen. 0 = av. <strong>Dette er en nødbrems, ikke en utrullingsmekanisme</strong> — les
            advarselen under før du setter den.
          </div>
          <div style="color:#a04000; font-size:0.8rem" class="mb-2">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
            <strong>Gulvet tar HELE terminalen ut av drift — ikke bare denne appen.</strong> Ligger enheten under gulvet,
            blir <em>alle</em> administrerte apper blokkert («Blocked by work policy»): både TastyPOS og Backoffice. Bare
            launcheren overlever, fordi Android ikke suspenderer hjem-appen. Verifisert på KASSE 1 2026-08-04 — der så det
            først ut som bare POS-appen var rammet, helt til Backoffice ble forsøkt åpnet.
          </div>
          <div style="color:#a04000; font-size:0.8rem" class="mb-2">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
            <strong>Og gulvet garanterer ikke at oppdateringen kommer.</strong> I samme test ble enheten stående på
            <code>installationFailureReason: IN_PROGRESS</code> i timevis uten at Play leverte — blokkert, men ikke
            oppdatert. Blir en enhet stående slik: sett tilbake til <strong>0</strong> og lagre, så låses appene opp igjen.
            Bruk «Høy prioritet» til vanlige utrullinger; sett gulvet kun som nødbrems for en kritisk fiks, utenom
            åpningstid, og når du vet at enhetene faktisk kan oppdatere.
          </div>
          <div style="color:var(--bo-muted); font-size:0.8rem" class="mb-2">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
            Oppdaterer en enhet seg likevel ikke? Play oppdaterer kun til et <em>høyere</em> versionCode, og aldri på tvers
            av signaturer. En enhet som har fått et debug-/adb-installert bygg blir derfor stående — avinstaller appen
            der (ev. via vedlikeholdsmodus), så henter Android Device Policy release-versjonen fra Play igjen.
          </div>
          <div v-if="!apps[role].length" class="text-muted fst-italic mb-2" style="font-size:0.85rem">
            Ingen apper i denne policyen ennå.
          </div>
          <div v-for="(app, idx) in apps[role]" :key="idx" class="mdm-app-row">
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <button class="mdm-icon-btn mdm-icon-btn--danger" title="Fjern app" @click="apps[role].splice(idx, 1)">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
              <input class="form-control form-control-sm" style="flex:1 1 220px; min-width:200px; max-width:340px"
                     v-model="app.packageName" placeholder="applicationId, f.eks. com.android.settings" />
              <select class="form-select form-select-sm" style="min-width:290px; max-width:340px" v-model="app.installType">
                <option v-for="t in installTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>
            </div>
            <div class="d-flex align-items-center gap-2 flex-wrap mt-2" style="margin-left:42px">
              <label class="mdm-app-sublabel">Oppdatering</label>
              <select class="form-select form-select-sm" style="max-width:210px" v-model="app.autoUpdateMode"
                      :title="updateModeHint(app.autoUpdateMode)">
                <option v-for="u in updateModes" :key="u.value" :value="u.value">{{ u.label }}</option>
              </select>
              <label class="mdm-app-sublabel ms-2">Min. versjon</label>
              <input type="number" min="0" class="form-control form-control-sm" style="max-width:120px"
                     v-model.number="app.minVersionCode" placeholder="0 = ingen"
                     :title="MIN_VERSION_HINT" />
            </div>
          </div>
          <button class="bo_btn_secondary mt-1" style="min-width:auto; font-size:0.82rem; padding:4px 12px"
                  @click="apps[role].push({ packageName: '', installType: 'FORCE_INSTALLED', autoUpdateMode: 'AUTO_UPDATE_DEFAULT', minVersionCode: 0 })">
            <font-awesome-icon :icon="['fas', 'plus']" class="me-1" /> Legg til app
          </button>

          <div class="fw-semibold mb-1 mt-4">Standard-app ved oppstart</div>
          <!-- Under CUSTOM_LAUNCHER the launcher hands over, and it counts from network, not from boot
               (TastyLAUNCHER HandoverController) — the case that left KASSE 1 off Sync Gateway on 2026-10-17. -->
          <div v-if="kinds[role] === 'CUSTOM_LAUNCHER'" style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
            TastyLAUNCHER starter appen én gang per oppstart. Sekundene telles fra enheten har nett, ikke fra den slås
            på. Uten nett står den på hjemskjermen og starter appen når nettet er tilbake. Pushes som managed config
            (<code>boot_launch_package</code>).
          </div>
          <div v-else style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
            Appen starter automatisk etter oppstart, med forsinkelse. Pushes som managed config
            (<code>boot_launch_package</code>) — CAGISTA-appen må honorere det (AMAPI kan ikke forsinke oppstart selv).
          </div>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <select class="form-select form-select-sm" style="max-width:280px" v-model="bootApp[role]">
              <option value="">Ingen</option>
              <option v-for="a in appPackages(role)" :key="'boot-' + a" :value="a">{{ a }}</option>
            </select>
            <span style="color:var(--bo-muted)">etter</span>
            <input type="number" min="0" max="120" class="form-control form-control-sm" style="max-width:90px"
                   v-model.number="bootDelay[role]" />
            <span style="color:var(--bo-muted)">{{ kinds[role] === 'CUSTOM_LAUNCHER' ? 'sek etter at enheten har nett' : 'sek' }}</span>
          </div>

          <!-- Only a CUSTOM_LAUNCHER policy has a launcher that can hand a guest back; the other kinds
               either lock with AMAPI or never lock at all. -->
          <template v-if="kinds[role] === 'CUSTOM_LAUNCHER'">
            <div class="fw-semibold mb-1 mt-4">Hent tilbake til appen</div>
            <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
              Forlater noen appen, sender launcheren dem tilbake etter denne tiden. Det er dette som gjør en
              selvbetjeningskiosk til en kiosk: navigasjonslinjen kan sveipes fram og Home trykkes, men du
              lander på en hjemskjerm som leverer deg rett tilbake. <b>0 = aldri</b> — bruk 0 på
              personalenheter, ellers blir en merchant kastet inn i appen igjen hver gang de går til
              hjemskjermen. Staben kommer forbi med kiosk-exit-PIN-en under.
            </div>
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <input type="number" min="0" max="600" class="form-control form-control-sm" style="max-width:90px"
                     v-model.number="returnSeconds[role]" />
              <span style="color:var(--bo-muted)">sek (0 = aldri)</span>
            </div>
          </template>

          <!-- Kiosk-exit PIN: hidden for MAINTENANCE-kind (already unlocked, nothing to exit) -->
          <template v-if="kinds[role] !== 'MAINTENANCE'">
            <div class="fw-semibold mb-1 mt-4">Lokal kiosk-exit (PIN)</div>
            <div style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
              <b>PIN-en er retten til å bli værende på hjemskjermen</b> — ikke retten til å komme dit.
              Veien ut av appen er alltid åpen (sveip fram navigasjonslinjen, trykk Home), men uten PIN
              blir enheten hentet tilbake etter tiden satt i «Hent tilbake til appen» over. Med PIN blir
              staben stående og kan konfigurere wifi og annet i fred.
              <br />
              Slik brukes den på enheten: <b>langtrykk hvor som helst på hjemskjermen</b> i ca. 1,5 sekund
              → tast PIN → «Lås opp». Et nytt langtrykk avslutter vinduet med én gang. Fem feil forsøk
              låser dialogen i 30 sekunder.
              <br />
              <b>Vinduet er hvor lenge merchanten eier hjemskjermen</b> — sett minutter, ikke sekunder;
              300 (5 min) er et fornuftig utgangspunkt. Kun hashen pushes — aldri rå PIN.
            </div>
            <div class="form-switch-wrapper mb-2" style="clear:both; overflow:auto; float:none">
              <label class="form-switch">
                <div class="form-switch-container">
                  <input type="checkbox" v-model="localExit[role].enabled" />
                  <i></i>
                  <span>Tillat lokal kiosk-exit</span>
                </div>
              </label>
            </div>
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <div style="position:relative; max-width:200px">
                <input :type="localExit[role].showPin ? 'text' : 'password'" inputmode="numeric" autocomplete="new-password"
                       class="form-control form-control-sm" style="padding-right:34px"
                       :placeholder="localExit[role].pinSet ? '•••• (lagret — la stå tomt)' : 'Ny PIN (4–8 siffer)'"
                       v-model="localExit[role].pin" />
                <button type="button" class="mdm-eye" :title="localExit[role].showPin ? 'Skjul PIN' : 'Vis PIN'"
                        @click="localExit[role].showPin = !localExit[role].showPin">
                  <font-awesome-icon :icon="['fas', localExit[role].showPin ? 'eye-slash' : 'eye']" />
                </button>
              </div>
              <span style="color:var(--bo-muted)">vindu</span>
              <input type="number" min="30" max="3600" class="form-control form-control-sm" style="max-width:90px"
                     v-model.number="localExit[role].relockSeconds" />
              <span style="color:var(--bo-muted)">sek (min. 30)</span>
            </div>
            <!-- The PIN alone does nothing: the server pushes the keys only when BOTH the switch is on and a
                 hash exists. Setting a PIN, saving, and finding that nothing reached the policy is a silent
                 failure, so say it here rather than let it be discovered in the raw JSON. -->
            <div v-if="!localExit[role].enabled && (localExit[role].pin || localExit[role].pinSet)"
                 class="alert alert-warning py-2 px-3 mt-2 mb-0" style="font-size:0.82rem">
              PIN-en lagres, men <b>pushes ikke</b> til policyen så lenge «Tillat lokal kiosk-exit» er av —
              enheten får ingen kiosk-exit. Slå på bryteren over.
            </div>
            <div v-else-if="localExit[role].enabled && !localExit[role].pin && !localExit[role].pinSet"
                 class="alert alert-warning py-2 px-3 mt-2 mb-0" style="font-size:0.82rem">
              Lokal kiosk-exit er på, men det er <b>ingen PIN</b> — hjørnet gjør ingenting før du setter en.
            </div>
            <div v-else-if="localExit[role].enabled && kinds[role] === 'CUSTOM_LAUNCHER' && !returnSeconds[role]"
                 class="alert alert-secondary py-2 px-3 mt-2 mb-0" style="font-size:0.82rem">
              Med «Hent tilbake til appen» = 0 har PIN-en ingenting å gjøre: den utsetter returen, og det er
              ingen retur. Sett en tid over, eller la kiosk-exit være av.
            </div>
          </template>
        </div>

          </div><!-- /.mdm-settings-card -->
          </config-lock>
        </div><!-- /.mdm-panels -->
      </div><!-- /.mdm-split -->
    </template>
  </div>

  <ConfirmDialogue ref="confirmDialog" />

  <popup-modal ref="rawModal">
    <h5 class="mb-1">Rå AMAPI-respons</h5>
    <div style="color:var(--bo-muted); font-size:0.85rem" class="mb-2">{{ rawTitle }}</div>
    <pre class="mdm-raw">{{ rawJson }}</pre>
    <div class="d-flex justify-content-end gap-2 mt-2">
      <button class="bo_btn_secondary" @click="copyRaw">{{ copied ? 'Kopiert ✓' : 'Kopier' }}</button>
      <button class="bo_btn" @click="$refs.rawModal.close()">Lukk</button>
    </div>
  </popup-modal>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import PopupModal from "@/components/other/PopupModal";
import ConfigLock from "@/components/other/ConfigLock";
import MdmService from "@/services/system/MdmService";
import { isLocalDev, refuseIfLocalDev } from "@/util/MdmLocalGuard";

export default {
  name: "MdmPolicies",
  components: { LoadingSpinner, ConfirmDialogue, PopupModal, ConfigLock },
  data() {
    return {
      loading: true,
      status: null,
      policies: [],
      policiesLocked: true,   // mirrors ConfigLock's state (gates outside-fieldset controls)
      rawJson: "",
      rawTitle: "",
      copied: false,
      // Dynamic policy list (names) + their kind, loaded from allSettings. Pre-load defaults mirror the backend
      // built-ins so the UI isn't empty before loadPolicySettings resolves.
      roles: ["POS_DESKTOP", "POS_MOBILE", "KIOSK", "KDS"],
      kinds: { POS_DESKTOP: "MULTI_APP", POS_MOBILE: "MULTI_APP", KIOSK: "SINGLE_APP", KDS: "SINGLE_APP" },
      // Create-policy form state
      showNewPolicy: false,
      // CUSTOM_LAUNCHER by default: every policy the fleet actually runs uses our own launcher
      // (TastyLAUNCHER owns the home screen). Fler-app Home is the Google launcher, which is the
      // exception, so it should be the deliberate choice rather than the one you get by not looking.
      newPolicy: { name: "", kind: "CUSTOM_LAUNCHER" },
      creatingPolicy: false,
      crudMessage: "",
      crudError: false,
      // Per-role screen orientation (managed config pushed on seed). Defaults mirror the backend.
      orientation: { POS_DESKTOP: "LANDSCAPE", POS_MOBILE: "PORTRAIT", KIOSK: "PORTRAIT", KDS: "LANDSCAPE" },
      // Per-role "hold the screen awake" (managed config keep_screen_on). Default ON, mirroring the backend.
      keepScreenOn: { POS_DESKTOP: true, POS_MOBILE: true, KIOSK: true, KDS: true },
      stayOnCharging: false,
      systemUpdate: { POS_DESKTOP: "WINDOWED", POS_MOBILE: "WINDOWED", KIOSK: "WINDOWED", KDS: "WINDOWED" },
      // Nav bar + Settings-app access per role (kioskCustomization). Defaults mirror the backend.
      systemNavigation: { POS_DESKTOP: "NAVIGATION_ENABLED", POS_MOBILE: "NAVIGATION_ENABLED", KIOSK: "NAVIGATION_DISABLED", KDS: "NAVIGATION_ENABLED" },
      deviceSettings: { POS_DESKTOP: "SETTINGS_ACCESS_ALLOWED", POS_MOBILE: "SETTINGS_ACCESS_ALLOWED", KIOSK: "SETTINGS_ACCESS_BLOCKED", KDS: "SETTINGS_ACCESS_BLOCKED" },
      // Per-policy maintenance link (name -> linked MAINTENANCE-kind policy, "" = none).
      maintenancePolicy: {},
      windowStartH: 2, windowStartM: 0,
      windowEndH: 4, windowEndM: 0,
      hourOptions: Array.from({ length: 24 }, (_, i) => i),
      minuteOptions: [0, 15, 30, 45],
      apps: { POS_DESKTOP: [], POS_MOBILE: [], KIOSK: [], KDS: [] },
      bootApp: { POS_DESKTOP: "", POS_MOBILE: "", KIOSK: "", KDS: "" },
      bootDelay: { POS_DESKTOP: 5, POS_MOBILE: 5, KIOSK: 5, KDS: 5 },
      returnSeconds: { POS_DESKTOP: 0, POS_MOBILE: 0, KIOSK: 0, KDS: 0 },
      localExit: {
        POS_DESKTOP: { enabled: false, pin: "", relockSeconds: 300, pinSet: false, showPin: false },
        POS_MOBILE: { enabled: false, pin: "", relockSeconds: 300, pinSet: false, showPin: false },
        KIOSK: { enabled: false, pin: "", relockSeconds: 300, pinSet: false, showPin: false },
        KDS: { enabled: false, pin: "", relockSeconds: 300, pinSet: false, showPin: false }
      },
      installTypes: [
        { value: "FORCE_INSTALLED", label: "Synlig i launcher (FORCE_INSTALLED)" },
        { value: "FORCE_INSTALLED_HIDDEN", label: "Installert, skjult (auto)" },
        { value: "AVAILABLE", label: "Tillatt, skjult (AVAILABLE)" },
        { value: "KIOSK", label: "Kiosk-app, fastlåst (KIOSK)" },
        { value: "BLOCKED", label: "Blokkert (BLOCKED)" }
      ],
      updateModes: [
        { value: "AUTO_UPDATE_DEFAULT", label: "Standard" },
        { value: "AUTO_UPDATE_POSTPONED", label: "Utsatt" },
        { value: "AUTO_UPDATE_HIGH_PRIORITY", label: "Høy prioritet (straks)" }
      ],
      // Same wording as the explanation above the app list, for the select's tooltip.
      updateModeHints: {
        AUTO_UPDATE_DEFAULT: "Standard: oppdaterer først når enheten er på Wi-Fi, lader, er inaktiv og appen ikke er i forgrunnen (sjekkes ca. daglig). På en kasse i daglig bruk kan en ny versjon bli liggende i dagevis.",
        AUTO_UPDATE_POSTPONED: "Utsatt: holder oppdateringer tilbake i opptil 90 dager, så installerer Play uansett. Dette er normalinnstillingen for flåten: pilot-policyen står på «Høy prioritet» og får releasen først, resten holdes igjen til den har stått et skift. Bremsen er en forsinkelse, ikke en lås — den kan ikke holde en merchant på en gammel versjon. «Tving oppdatering» går uansett gjennom, siden det er en installasjon og ikke en oppdatering.",
        AUTO_UPDATE_HIGH_PRIORITY: "Høy prioritet: rulles ut så snart Play ser releasen. Appen kan bli stoppet og oppdatert mens den er i bruk — legg releaser utenom rush."
      },
      MIN_VERSION_HINT: "Nødbrems, ikke utrullingsmekanisme. Hardt gulv: enheten er ikke-etterlevende til den har minst denne versjonen. ADVARSEL: da blokkeres ALLE administrerte apper på enheten — både TastyPOS og Backoffice, ikke bare denne — og terminalen er ute av drift til oppdateringen er inne. Gulvet garanterer heller ikke at Play faktisk leverer. 0 = av.",
      activeTab: "general",
      policySearch: "",       // filters the policy navigator (matches base names AND their variants)
      expandedBases: {},      // policy name -> variants shown; collapsed by default, so the list stays short
      savingOrientation: false,
      orientationMessage: "",
      orientationError: false,
      // The 06:00 restart of tills, kiosks and KDS — its own document and endpoint, saved at once (toggleDailyReboot).
      dailyReboot: { loaded: false, enabled: true, time: "06:00", policies: [], changedAt: "", changedBy: "", lastRun: null },
      dailyRebootLoadError: "",
      savingDailyReboot: false,
      dailyRebootMessage: "",
      dailyRebootError: false
    };
  },
  computed: {
    updateWindowStart() { return this.pad2(this.windowStartH) + ":" + this.pad2(this.windowStartM); },
    updateWindowEnd() { return this.pad2(this.windowEndH) + ":" + this.pad2(this.windowEndM); },
    // Already configured (→ starts locked): policies have been seeded into AMAPI, so the settings are live.
    isConfigured() { return this.policies.length > 0; },
    // Client-side mirror of the backend name rule (^[A-Z][A-Z0-9_]{1,30}$, not *_PROV, unique).
    newPolicyNameValid() {
      const n = (this.newPolicy.name || "").trim().toUpperCase();
      if (!/^[A-Z][A-Z0-9_]{1,30}$/.test(n)) return false;
      if (n.endsWith("_PROV")) return false;
      return !this.roles.includes(n);
    },
    // Maintenance-kind policy names (options for the per-policy "Vedlikeholdspolicy" link).
    maintenanceOptions() {
      return this.roles.filter((r) => this.kinds[r] === "MAINTENANCE");
    },
    /**
     * The navigator's model: every managed policy (already sorted by name) with the auto-generated
     * policies that belong to it nested underneath, plus the ones whose base policy is gone.
     *
     * The fleet generates one or two twins per policy (<name>_PROV, <name>_UPDATE), so a flat list is
     * two to three times longer than the number of policies an admin actually maintains. Nesting them
     * means the list length tracks the policies, not the twins.
     */
    policyTree() {
      const sys = this.systemPolicies;
      const owned = new Set();
      const tree = this.roles.map((name) => {
        const variants = sys.filter((v) => this.baseOf(v.name) === name);
        variants.forEach((v) => owned.add(v.name));
        return { name, variants };
      });
      return { tree, orphans: sys.filter((v) => !owned.has(v.name)) };
    },
    // A policy stays in the list if its own name matches OR one of its variants does (then only the
    // matching variants are shown, and the node auto-expands — see isBaseExpanded).
    filteredTree() {
      const q = this.policySearch.trim();
      if (!q) return this.policyTree.tree;
      return this.policyTree.tree
        .map((node) => {
          if (this.policyMatches(node.name, q)) return node;
          const variants = node.variants.filter((v) => this.policyMatches(v.name, q));
          return variants.length ? { name: node.name, variants } : null;
        })
        .filter(Boolean);
    },
    filteredOrphans() {
      const q = this.policySearch.trim();
      return q ? this.policyTree.orphans.filter((v) => this.policyMatches(v.name, q)) : this.policyTree.orphans;
    },
    // The policies the 06:00 restart reaches, by name — the server derives them from each policy's apps, and says
    // whether each one's app comes back by itself after the restart.
    dailyRebootPolicies() {
      const p = Array.isArray(this.dailyReboot.policies) ? this.dailyReboot.policies : [];
      return p.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "", "nb"));
    },
    dailyRebootChangedLabel() {
      const d = this.dailyReboot;
      if (!d.changedAt) return "";
      return " " + this.formatWhen(d.changedAt) + (d.changedBy ? " av " + d.changedBy : "");
    },
    dailyRebootFailures() {
      const run = this.dailyReboot.lastRun;
      return run && Array.isArray(run.failures) ? run.failures : [];
    },
    dailyRebootLastRunBad() {
      const run = this.dailyReboot.lastRun;
      return !!(run && !run.switchedOff && (run.failed > 0 || run.listingError));
    },
    dailyRebootLastRunLabel() {
      const run = this.dailyReboot.lastRun;
      if (!run) return "Har ikke kjørt ennå.";
      const when = this.formatWhen(run.at);
      if (run.switchedOff) return when + ": avslått, ingenting sendt.";
      const sent = (run.sent || 0) + " sendt (" + this.plural(run.tills || 0, "kasse", "kasser") + ", "
        + this.plural(run.kiosks || 0, "kiosk", "kiosker") + ", " + this.plural(run.kds || 0, "KDS", "KDS") + ")";
      const rest = (run.failed || 0) + " feilet, " + (run.skipped || 0) + " hoppet over, av "
        + this.plural(run.devices || 0, "enhet", "enheter") + " i MDM.";
      if (run.listingError) {
        return when + ": stoppet, fikk ikke lest enhetslista (" + run.listingError + "). Før det: " + sent + ", " + rest;
      }
      return when + ": " + sent + ", " + rest;
    },
    // AMAPI policies that are NOT editable managed ones — the auto-generated *_PROV variants.
    // Shown READ-ONLY in the overview so every policy name is visible; they're system-managed (no structured form).
    systemPolicies() {
      const managed = new Set(this.roles);
      return (this.policies || [])
        .map((p) => ({ raw: p, name: this.shortName(p.name, "policies") }))
        .filter((x) => x.name && !managed.has(x.name))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  },
  mounted() {
    MdmService.getStatus().then(
      (response) => {
        this.status = response.data;
        this.loading = false;
        if (this.status.configured) { this.loadPolicies(); this.loadPolicySettings(); this.loadDailyReboot(); }
      },
      (error) => {
        console.log(error);
        this.status = { configured: false };
        this.loading = false;
      }
    );
  },
  methods: {
    isLocalDev,
    // Unlike the device page there is no PROD marker to distinguish by: on a shared enterprise EVERY
    // policy is a production policy.
    refuseIfLocalDev() {
      return refuseIfLocalDev(this.$refs.confirmDialog,
        "Policyer kan leses herfra, men ikke opprettes, endres eller slettes: en lagring pusher policyene " +
        "ut fra dette miljøets innstillinger, og hele flåten tar dem i bruk ved neste synk.");
    },
    updateModeHint(mode) {
      return this.updateModeHints[mode] || "";
    },
    orientationLabel(v) {
      return v === "PORTRAIT" ? "Stående" : v === "AUTO" ? "Automatisk" : "Liggende";
    },
    pad2(n) {
      return String(n).padStart(2, "0");
    },
    snapMinute(m) {
      // Snap a stored minute to the nearest dropdown option (so any legacy value still selects cleanly).
      return this.minuteOptions.reduce((best, o) => (Math.abs(o - m) < Math.abs(best - m) ? o : best), 0);
    },
    appCount(role) {
      return (this.apps[role] || []).filter((a) => a.packageName && a.packageName.trim()).length;
    },
    appPackages(role) {
      return (this.apps[role] || []).map((a) => (a.packageName || "").trim()).filter(Boolean);
    },
    hasHiddenApp(role) {
      // Any FORCE_INSTALLED_HIDDEN app → this role gets a *_PROV provisioning policy (install-then-hide).
      return (this.apps[role] || []).some((a) => a.installType === "FORCE_INSTALLED_HIDDEN" && (a.packageName || "").trim());
    },
    // The managed policy an auto-generated one belongs to (KIOSK_PILOT_PROV -> KIOSK_PILOT), or null.
    baseOf(name) {
      if (!name) return null;
      if (name.endsWith("_PROV")) return name.slice(0, -5);
      if (name.endsWith("_UPDATE")) return name.slice(0, -7);
      return null;
    },
    // Nested under their base, the suffix alone identifies a variant — the full name is in the tooltip.
    variantSuffix(name) {
      const base = this.baseOf(name);
      return base ? name.slice(base.length) : name;
    },
    variantLabel(name) {
      if (name.endsWith("_PROV")) return "provisjonering";
      if (name.endsWith("_UPDATE")) return "oppdatering";
      return "system";
    },
    // Every term must appear in the name; the query is split on spaces AND underscores, so "pos pilot",
    // "pos_pilot" and "pilot desktop" all find POS_DESKTOP_PILOT.
    policyMatches(name, query) {
      const n = (name || "").toUpperCase();
      return query.toUpperCase().split(/[\s_]+/).filter(Boolean).every((t) => n.includes(t));
    },
    toggleBase(name) {
      this.expandedBases[name] = !this.expandedBases[name];
    },
    isBaseExpanded(node) {
      if (!node.variants.length) return false;
      // Matched through a variant, or one of them is open: hiding it would hide the reason it is listed.
      const q = this.policySearch.trim();
      if (q && !this.policyMatches(node.name, q)) return true;
      if (node.variants.some((v) => v.name === this.activeTab)) return true;
      return !!this.expandedBases[node.name];
    },
    kindLabel(kind) {
      if (kind === "MAINTENANCE") return "Vedlikehold";
      if (kind === "CUSTOM_LAUNCHER") return "Egen launcher";
      return kind === "SINGLE_APP" ? "Enkelt-app kiosk mode" : "Fler-app Home (Google Launcher)";
    },
    async openNewPolicy() {
      if (await this.refuseIfLocalDev()) return;
      this.newPolicy = { name: "", kind: "CUSTOM_LAUNCHER" };
      this.crudMessage = "";
      this.showNewPolicy = true;
    },
    async createPolicy() {
      if (await this.refuseIfLocalDev()) return;
      if (!this.newPolicyNameValid) return;
      this.creatingPolicy = true;
      this.crudMessage = "";
      this.crudError = false;
      const name = this.newPolicy.name.trim().toUpperCase();
      MdmService.createPolicy(name, this.newPolicy.kind).then(
        (response) => {
          this.creatingPolicy = false;
          this.showNewPolicy = false;
          this.applySettings(response.data);   // re-hydrate maps from the returned allSettings
          this.activeTab = name;               // jump to the new policy's panel
          this.crudMessage = `Policy ${name} opprettet.`;
          this.loadPolicies();
        },
        (error) => {
          this.creatingPolicy = false;
          this.crudError = true;
          this.crudMessage = this.errMsg(error, "Kunne ikke opprette policy.");
        }
      );
    },
    async deletePolicyStructured(role) {
      if (await this.refuseIfLocalDev()) return;
      const ok = await this.$refs.confirmDialog.show({
        title: "Slett policy",
        message: `Slette policy <code>${role}</code>? Fjernes fra AMAPI og innstillingene. ` +
          `Avvises hvis en enhet fortsatt bruker den, eller den er koblet som vedlikeholdspolicy.`,
        okButton: "Slett", cancelButton: "Avbryt",
        requireText: role
      });
      if (!ok) return;
      this.crudMessage = "";
      this.crudError = false;
      MdmService.deleteManagedPolicy(role).then(
        (response) => {
          this.applySettings(response.data);
          if (this.activeTab === role) this.activeTab = "general";
          this.crudMessage = `Policy ${role} slettet.`;
          this.loadPolicies();
        },
        (error) => {
          this.crudError = true;
          this.crudMessage = this.errMsg(error, "Kunne ikke slette policy.");   // incl. the in-use guard message
        }
      );
    },
    /**
     * Removes a leftover «Tving oppdatering» variant (<base>_UPDATE) from AMAPI.
     *
     * A variant is created against whichever policy the device was on, so testing from a maintenance
     * policy leaves its own twin behind. Nothing depends on them — the next forced update rebuilds the
     * one it needs — so this only tidies the list. AMAPI itself refuses while a device or enrolment
     * token still references the policy, which is the guard that matters; its message is surfaced as-is.
     */
    async deleteVariantPolicy(name) {
      if (await this.refuseIfLocalDev()) return;
      const base = name.slice(0, -7);
      const ok = await this.$refs.confirmDialog.show({
        title: "Slett rest-policy",
        message: `Slette <code>${name}</code>? Den er en rest etter «Tving oppdatering» på ` +
          `<code>${base}</code>, og lages på nytt neste gang den trengs. ` +
          `Avvises hvis en enhet fortsatt står på den.`,
        okButton: "Slett", cancelButton: "Avbryt"
      });
      if (!ok) return;
      this.crudMessage = "";
      this.crudError = false;
      MdmService.deletePolicy(name).then(
        () => {
          if (this.activeTab === name) this.activeTab = "general";
          this.crudMessage = `Policy ${name} slettet.`;
          this.loadPolicies();
        },
        (error) => {
          this.crudError = true;
          this.crudMessage = this.errMsg(error, "Kunne ikke slette policyen. Står en enhet fortsatt på den?");
        }
      );
    },
    shortName(name, segment) {
      if (!name) return "";
      const marker = "/" + segment + "/";
      const i = name.indexOf(marker);
      return i >= 0 ? name.substring(i + marker.length) : name;
    },
    loadPolicies() {
      MdmService.listPolicies().then(
        (response) => { this.policies = Array.isArray(response.data.policies) ? response.data.policies : []; },
        (error) => { console.log(error); }
      );
    },
    loadPolicySettings() {
      MdmService.getPolicySettings().then(
        (response) => { this.applySettings(response.data); },
        (error) => { console.log(error); }
      );
    },
    // Re-hydrate every per-policy map from an allSettings payload. Drives the DYNAMIC policy list off data.kinds
    // (falls back to the 4 built-ins), so create/delete just re-apply the returned settings.
    applySettings(data) {
      data = data || {};
      // Trust the server's dynamic list (allSettings always returns kinds; blank start → {} → no policies).
      // The data() pre-load defaults cover the pre-fetch render only.
      const kinds = data.kinds || {};
      const names = Object.keys(kinds);
      const o = data.orientation || {}, su = data.systemUpdate || {}, sn = data.systemNavigation || {},
        ds = data.deviceSettings || {}, ap = data.apps || {}, bl = data.bootLaunch || {}, lx = data.localExit || {},
        mp = data.maintenancePolicy || {}, kso = data.keepScreenOn || {};
      const orientation = {}, systemUpdate = {}, systemNavigation = {}, deviceSettings = {},
        apps = {}, bootApp = {}, bootDelay = {}, returnSeconds = {}, localExit = {}, maintenancePolicy = {}, keepScreenOn = {};
      names.forEach((r) => {
        orientation[r] = o[r] || "LANDSCAPE";
        keepScreenOn[r] = kso[r] !== false;   // default ON, matching the backend
        systemUpdate[r] = su[r] || "WINDOWED";
        systemNavigation[r] = sn[r] || "NAVIGATION_ENABLED";
        deviceSettings[r] = ds[r] || "SETTINGS_ACCESS_BLOCKED";
        maintenancePolicy[r] = mp[r] || "";
        apps[r] = Array.isArray(ap[r]) ? ap[r].map((a) => ({
          packageName: a.packageName || "",
          installType: a.installType || "FORCE_INSTALLED",
          autoUpdateMode: a.autoUpdateMode || "AUTO_UPDATE_DEFAULT",
          minVersionCode: a.minVersionCode || 0
        })) : [];
        bootApp[r] = bl[r] ? (bl[r].app || "") : "";
        bootDelay[r] = bl[r] && typeof bl[r].delaySeconds === "number" ? bl[r].delaySeconds : 5;
        returnSeconds[r] = bl[r] && typeof bl[r].returnSeconds === "number" ? bl[r].returnSeconds : 0;
        localExit[r] = {
          enabled: !!(lx[r] && lx[r].enabled),
          relockSeconds: (lx[r] && typeof lx[r].relockSeconds === "number") ? lx[r].relockSeconds : 300,
          pinSet: !!(lx[r] && lx[r].pinSet),
          pin: "",
          showPin: false
        };
      });
      // Sorted by name: the navigator, the overview table and the panels all read this list, and the
      // stored order is creation order, which tells the admin nothing.
      this.roles = names.sort((a, b) => a.localeCompare(b, "nb"));
      this.kinds = kinds;
      this.orientation = orientation;
      this.keepScreenOn = keepScreenOn;
      this.systemUpdate = systemUpdate;
      this.systemNavigation = systemNavigation;
      this.deviceSettings = deviceSettings;
      this.maintenancePolicy = maintenancePolicy;
      this.apps = apps;
      this.bootApp = bootApp;
      this.bootDelay = bootDelay;
      this.returnSeconds = returnSeconds;
      this.localExit = localExit;
      this.stayOnCharging = !!data.stayOnWhileCharging;
      const uw = data.updateWindow || {};
      if (typeof uw.start === "number") { this.windowStartH = Math.floor(uw.start / 60); this.windowStartM = this.snapMinute(uw.start % 60); }
      if (typeof uw.end === "number") { this.windowEndH = Math.floor(uw.end / 60); this.windowEndM = this.snapMinute(uw.end % 60); }
      // If the open tab's policy was deleted, fall back to Generelt.
      if (this.activeTab !== "general" && !names.includes(this.activeTab)) this.activeTab = "general";
    },
    async saveSettings() {
      if (await this.refuseIfLocalDev()) return;
      this.savingOrientation = true;
      this.orientationMessage = "";
      this.orientationError = false;
      // Persist orientation per role + the stay-awake toggle, then re-seed so the changes reach AMAPI.
      const saves = this.roles.map((r) => MdmService.setPolicyOrientation(r, this.orientation[r]));
      this.roles.forEach((r) => saves.push(MdmService.setKeepScreenOn(r, this.keepScreenOn[r])));
      this.roles.forEach((r) => saves.push(MdmService.setSystemUpdateMode(r, this.systemUpdate[r])));
      this.roles.forEach((r) => saves.push(MdmService.setSystemNavigation(r, this.systemNavigation[r])));
      this.roles.forEach((r) => saves.push(MdmService.setDeviceSettings(r, this.deviceSettings[r])));
      this.roles.forEach((r) => saves.push(MdmService.setApps(r, this.apps[r])));
      this.roles.forEach((r) => saves.push(MdmService.setBootLaunch(r, this.bootApp[r], this.bootDelay[r], this.returnSeconds[r])));
      this.roles.forEach((r) => saves.push(MdmService.setLocalExit(r, this.localExit[r].enabled, this.localExit[r].pin, this.localExit[r].relockSeconds)));
      // Maintenance link only applies to non-maintenance policies (the ones that can enter maintenance).
      this.roles.filter((r) => this.kinds[r] !== "MAINTENANCE")
        .forEach((r) => saves.push(MdmService.setMaintenancePolicy(r, this.maintenancePolicy[r] || "")));
      saves.push(MdmService.setUpdateWindow(this.windowStartH * 60 + this.windowStartM, this.windowEndH * 60 + this.windowEndM));
      saves.push(MdmService.setStayOnCharging(this.stayOnCharging));
      Promise.all(saves).then(
        () => MdmService.seedDefaultPolicies().then(
          () => {
            this.savingOrientation = false;
            this.orientationMessage = "Lagret. Policyene er oppdatert — enhetene tar det i bruk ved neste synk.";
            // A saved PIN is now stored server-side; clear the input and mark it set.
            this.roles.forEach((r) => { if (this.localExit[r].pin) { this.localExit[r].pinSet = true; this.localExit[r].pin = ""; } });
            this.loadPolicies();
            this.$refs.lock.relock();   // re-lock after a successful save
          },
          (error) => {
            this.savingOrientation = false;
            this.orientationError = true;
            this.orientationMessage = this.errMsg(error, "Innstillinger lagret, men policyene ble ikke oppdatert.");
          }
        ),
        (error) => {
          this.savingOrientation = false;
          this.orientationError = true;
          this.orientationMessage = this.errMsg(error, "Kunne ikke lagre innstillinger.");
        }
      );
    },
    loadDailyReboot() {
      MdmService.getDailyReboot().then(
        (response) => { this.applyDailyReboot(response.data); },
        (error) => { this.dailyRebootLoadError = this.errMsg(error, "Kunne ikke lese innstillingen for daglig omstart."); }
      );
    },
    applyDailyReboot(data) {
      data = data || {};
      this.dailyReboot = {
        loaded: true,
        enabled: data.enabled !== false,
        time: data.time || "06:00",
        policies: Array.isArray(data.policies) ? data.policies : [],
        changedAt: data.changedAt || "",
        changedBy: data.changedBy || "",
        lastRun: data.lastRun || null
      };
      this.dailyRebootLoadError = "";
    },
    /**
     * Saves the switch at once, after a confirmation that says what it does. Until the server confirms, the switch
     * keeps showing the saved state, so a refused or failed save never looks like a change that took.
     */
    async toggleDailyReboot(event) {
      const wanted = event.target.checked;
      event.target.checked = this.dailyReboot.enabled;
      if (await this.refuseIfLocalDev()) return;
      const time = this.dailyReboot.time;
      const ok = await this.$refs.confirmDialog.show({
        title: wanted ? "Slå på daglig omstart" : "Slå av daglig omstart",
        message: wanted
          ? `Fra neste kjøring kl. ${time} sendes «Reboot» til alle kasser, kiosker og kjøkkenskjermer (KDS), hver morgen.`
          : `Kasser, kiosker og kjøkkenskjermer startes ikke lenger på nytt kl. ${time}, før noen slår det på igjen.`,
        okButton: wanted ? "Slå på" : "Slå av",
        cancelButton: "Avbryt"
      });
      if (!ok) return;
      this.savingDailyReboot = true;
      this.dailyRebootMessage = "";
      this.dailyRebootError = false;
      MdmService.setDailyReboot(wanted).then(
        (response) => {
          this.savingDailyReboot = false;
          this.applyDailyReboot(response.data);
          this.dailyRebootMessage = wanted ? `Slått på. Neste kjøring er kl. ${time}.` : "Slått av.";
        },
        (error) => {
          this.savingDailyReboot = false;
          this.dailyRebootError = true;
          this.dailyRebootMessage = this.errMsg(error, "Kunne ikke lagre innstillingen. Den er uendret.");
        }
      );
    },
    rebootRoleLabel(role) {
      return role === "till" ? "kasse" : role === "kiosk" ? "kiosk" : "kjøkkenskjerm";
    },
    // What it costs when the app does not come back by itself after the restart.
    rebootNoStartText(role) {
      if (role === "till") return "TastyPOS starter ikke av seg selv: kassa står på hjemskjermen til noen åpner appen.";
      if (role === "kiosk") return "TastyKIOSK starter ikke av seg selv: kiosken står på hjemskjermen til noen åpner appen.";
      return "TastyKDS starter ikke av seg selv: kjøkkenskjermen viser ingen ordre før noen åpner appen.";
    },
    // "tir. 22.10 kl. 06:00" from an ISO instant.
    formatWhen(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso || "";
      return d.toLocaleDateString("nb-NO", { weekday: "short", day: "2-digit", month: "2-digit" })
        + " kl. " + d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
    },
    plural(n, one, many) {
      return n + " " + (n === 1 ? one : many);
    },
    // The raw AMAPI policy object for a managed policy name (from the loaded AMAPI list), or null when it
    // hasn't been pushed to AMAPI yet (created but not saved).
    rawPolicyFor(name) {
      return (this.policies || []).find((p) => this.shortName(p.name, "policies") === name) || null;
    },
    showRawPolicy(name) {
      const raw = this.rawPolicyFor(name);
      if (raw) { this.showRaw(raw, name); }
    },
    showRaw(obj, title) {
      this.rawTitle = title || "";
      this.rawJson = JSON.stringify(obj, null, 2);
      this.copied = false;
      this.$refs.rawModal.open();
    },
    copyRaw() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.rawJson).then(() => { this.copied = true; }).catch(() => {});
      }
    },
    errMsg(error, fallback) {
      const data = error && error.response ? error.response.data : null;
      return (data && data.error) || fallback;
    }
  }
};
</script>

<style scoped>
.mdm-back {
  border: none;
  background: none;
  color: var(--bo-brand-ink);
  padding: 0;
  font-size: 0.9rem;
  cursor: pointer;
}
.mdm-back:hover {
  text-decoration: underline;
}
.mdm-enterprise {
  color: var(--bo-muted);
  font-size: 0.9rem;
  white-space: nowrap;
}

.mdm-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--bo-line);
  background: var(--bo-surface);
  color: var(--bo-ink-2);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.mdm-icon-btn:hover:not(:disabled) {
  background: var(--bo-surface-2);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}
.mdm-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.mdm-icon-btn--danger {
  color: var(--bo-alert-ink);
  border-color: #e7c3bd;
}
.mdm-icon-btn--danger:hover:not(:disabled) {
  background: var(--bo-bad-bg);
}

.policy-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
  max-width: 380px;
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  overflow: hidden;
}
.policy-list__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.45rem 0.6rem;
  border-bottom: 1px solid var(--bo-surface-2);
}
.policy-list__item:last-child {
  border-bottom: none;
}
.policy-list__item code {
  font-size: 0.95rem;
}
.policy-list__empty {
  color: var(--bo-muted);
  padding: 0.5rem 0.75rem;
}
.mdm-settings-card {
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  padding: 1rem 1.25rem;
  max-width: 100%;
}
/* Keep the title + save button visible while scrolling the (tall) settings card */
.mdm-settings-head {
  position: sticky;
  top: 0;
  z-index: 5;
  background: var(--bo-surface);
  margin: -1rem -1.25rem 0.75rem;
  padding: 0.7rem 1.25rem;
  border-bottom: 1px solid var(--bo-line);
  border-radius: 8px 8px 0 0;
}
.mdm-orient-card__title {
  font-weight: 600;
  font-size: 1.05rem;
}

/* Policy navigator beside the selected policy's panel (replaces a tab strip that wrapped over six rows) */
.mdm-split {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
}
.mdm-panels {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 860px;
}
.mdm-nav {
  /* Wide enough for the longest name the fleet actually uses (POS_DESKTOP_MAINTENANCE, 23 chars).
     Names may run to 31 characters, and those wrap onto a second line rather than being cut — see
     .mdm-navitem__name. Nothing here scrolls sideways. */
  flex: 0 0 300px;
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.55rem;
  border: 1px solid #e9e9ec;
  border-radius: 8px;
  background: var(--bo-surface-2);
  /* Follows the panel down: the per-policy forms are long, and scrolling one should not cost you the
     way back to the others. */
  position: sticky;
  /* The page scrolls, not this column, so it has to clear the pinned navbar — and lose the same
     height from its own cap, or the bottom of the list ends up below the fold. */
  top: calc(var(--bo-nav-offset, 0px) + 12px);
  max-height: calc(100vh - var(--bo-nav-offset, 0px) - 40px);
}
.mdm-nav__clear {
  font-size: 15px;
  right: 8px;
}
.mdm-nav__count {
  color: #9aa0a6;
  font-size: 0.72rem;
  padding: 0 0.2rem 0.1rem;
}
.mdm-nav__list {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 -0.2rem;
  padding: 0 0.2rem;
}
.mdm-nav__empty {
  color: var(--bo-muted);
  font-size: 0.82rem;
  font-style: italic;
  padding: 0.45rem 0.35rem;
}
.mdm-nav__group {
  color: #9aa0a6;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0.6rem 0 0.15rem 0.35rem;
}
.mdm-navrow {
  display: flex;
  align-items: center;
}
.mdm-nav__caret {
  flex: 0 0 18px;
  width: 18px;
  height: 28px;
  padding: 0;
  border: none;
  background: none;
  color: #b0b3b8;
  font-size: 0.7rem;
  cursor: pointer;
}
.mdm-nav__caret:hover {
  color: var(--bo-ink-2);
}
.mdm-nav__caret--hidden {
  visibility: hidden;
  cursor: default;
}
.mdm-navitem {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  min-width: 0;
  padding: 5px 8px;
  border: none;
  border-left: 3px solid transparent;
  border-radius: 6px;
  background: none;
  text-align: left;
  color: var(--bo-ink-2);
  font-size: 0.82rem;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.mdm-navitem:hover {
  background: var(--bo-surface-2);
  color: var(--bo-ink);
}
.mdm-navitem--active {
  background: var(--bo-brand-tint);
  border-left-color: #ECB109;
  color: var(--bo-brand-ink);
  font-weight: 600;
}
.mdm-navitem__name {
  flex: 1 1 auto;
  min-width: 0;
  /* Wrap rather than ellipsize: a truncated policy name is unreadable — POS_DESKTOP_MAINTENANCE and
     POS_DESKTOP_PILOT_UPDATE differ only at the end. `anywhere` lets it break at the underscores. */
  overflow-wrap: anywhere;
  line-height: 1.3;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
}
.mdm-navitem__meta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  color: #a3a7ab;
  font-size: 0.71rem;
  font-weight: 400;
}
.mdm-navitem__metaicon {
  font-size: 0.62rem;
  opacity: 0.75;
}
.mdm-navitem__kind {
  color: #9aa0a6;
  font-size: 0.72rem;
}
.mdm-navitem--general .mdm-navitem__name {
  font-family: inherit;
  font-size: 0.85rem;
}
/* Auto-generated twin, indented under the policy it belongs to */
.mdm-navitem--variant {
  width: calc(100% - 18px);
  margin-left: 18px;
  color: #8a8f94;
  font-style: italic;
}
.mdm-navitem--variant .mdm-navitem__name {
  font-size: 0.74rem;
}
.mdm-navitem--orphan {
  width: 100%;
  margin-left: 0;
}
.mdm-nav__add {
  flex: 0 0 auto;
  width: 100%;
  padding: 5px 8px;
  border: 1px dashed #cfd4d9;
  border-radius: 6px;
  background: var(--bo-surface);
  color: var(--bo-success-ink);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.mdm-nav__add:hover:not(:disabled) {
  border-color: #2f7d32;
  background: var(--bo-surface-2);
}
.mdm-nav__add:disabled {
  color: #9aa0a6;
  border-style: dashed;
  cursor: not-allowed;
  opacity: 0.7;
}

/* A policy's auto-generated twins, in the Generelt overview table */
.mdm-varchip {
  display: inline-block;
  margin-right: 4px;
  padding: 0 7px;
  border: 1px solid var(--bo-line);
  border-radius: 999px;
  background: var(--bo-surface-2);
  color: var(--bo-muted);
  font-size: 0.7rem;
  cursor: pointer;
}
.mdm-varchip:hover {
  border-color: #ECB109;
  background: var(--bo-brand-tint);
  color: var(--bo-brand-ink);
}

@media (max-width: 991px) {
  .mdm-split {
    display: block;
  }
  .mdm-nav {
    position: static;
    width: 100%;
    max-height: none;
    margin-bottom: 1rem;
  }
  .mdm-nav__list {
    max-height: 320px;
  }
  .mdm-panels {
    max-width: none;
  }
  /* Roomier rows for touch */
  .mdm-navitem {
    padding: 9px 8px;
  }
  .mdm-nav__caret {
    height: 38px;
  }
}

/* Inline create-policy form */
.mdm-newpolicy {
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  background: var(--bo-surface-2);
}

/* Localhost-against-production notice, mirroring the device page */
.mdm-devbanner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 680px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.84rem;
  font-style: normal;
  color: var(--bo-brand-ink);
  background: var(--bo-brand-tint);
  border: 1px solid #ffd591;
}

/* A read-only action styled as a button. An <a> is not disabled by the ConfigLock fieldset, so viewing
   the raw AMAPI policy survives the lock — reading was never what the lock is there to prevent. */
.mdm-btn-a {
  display: inline-block;
  text-align: center;
  text-decoration: none;
}
.mdm-btn-a--off {
  cursor: default;
  color: #BBBBBB;
  border-color: var(--bo-line);
  pointer-events: none;
}

/* The open policy's name, at the top of its panel */
.mdm-panel-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--bo-ink);
}

/* Read-only policy-kind pill shown on each policy panel */
.mdm-kind-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--bo-surface-2);
  color: #4a5568;
  font-size: 0.78rem;
  font-weight: 600;
}

/* Per-policy delete button */
.mdm-del-policy {
  min-width: auto;
  font-size: 0.82rem;
  padding: 4px 12px;
  color: var(--bo-alert-ink);
  border-color: #e7c3bd;
}
.mdm-del-policy:hover {
  background: var(--bo-bad-bg);
}

/* At-a-glance overview table (Generelt tab) */
.mdm-overview {
  font-size: 0.88rem;
}
.mdm-overview th {
  color: var(--bo-muted);
  font-weight: 600;
}
.mdm-overview tbody tr:hover {
  background: var(--bo-surface-2);
}

/* One app in the per-policy app list — a card: package + install type, then update controls */
.mdm-app-row {
  background: var(--bo-surface-2);
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 0.6rem 0.7rem;
  margin-bottom: 0.5rem;
}
.mdm-app-sublabel {
  margin: 0;
  color: var(--bo-muted);
  font-size: 0.78rem;
  white-space: nowrap;
}

/* Show/hide toggle inside the PIN input */
.mdm-eye {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  color: var(--bo-muted);
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  font-size: 0.85rem;
}
.mdm-eye:hover {
  color: var(--bo-ink-2);
}
.mdm-raw {
  background: var(--bo-surface-2);
  border: 1px solid var(--bo-line);
  color: #24292e;
  padding: 0.75rem;
  border-radius: 8px;
  max-height: 60vh;
  overflow: auto;
  font-size: 0.78rem;
  white-space: pre;
  margin: 0;
}

/* «Daglig omstart» on Generelt: what the 06:00 restart reaches and how the last run went */
.mdm-reboot {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  max-width: 760px;
}
.mdm-reboot__off {
  background: var(--bo-warn-bg);
  color: var(--bo-warn-fg);
  border: 1px solid var(--bo-warn-line);
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
}
.mdm-reboot__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem 0.5rem;
}
.mdm-reboot__label {
  flex: 0 0 auto;
  min-width: 96px;
  color: var(--bo-muted);
}
.mdm-reboot__policies {
  margin: 0 0 0 1.1rem;
  padding: 0;
}
.mdm-reboot__policylink {
  cursor: pointer;
}
.mdm-reboot__policylink:hover code {
  text-decoration: underline;
}
.mdm-reboot__warn {
  color: var(--bo-warn-fg);
}
.mdm-reboot__hint {
  color: var(--bo-muted);
  font-size: 0.8rem;
}
.mdm-reboot__bad {
  color: var(--bo-bad-fg);
}
.mdm-reboot__failures {
  margin: 0 0 0 1.1rem;
  padding: 0;
  color: var(--bo-bad-fg);
}
</style>
